import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Switch,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  TableContainer,
  Text,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  ListItem,
  UnorderedList,
  Input,
  useToast,
} from '@chakra-ui/react';
import {
  faCrown,
  faMapMarkedAlt,
  faEdit,
  faLock,
  faUserLock,
  faPaperPlane,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import styled from 'styled-components';

import TruckersMap from './TruckersMap';
import EditTruckerModal from './EditTruckerModal';
import AddTrucker from '../AddTrucker';
import client from '../../../api/api';
import InfoBar from '../../InfoBar/InfoBar';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;

  .icon,
  .all-lock-icon {
    color: #3182ce;
    width: 1rem;
    height: 1rem;
    margin: 0 14px;
  }

  .all-lock-icon {
    margin: 0 0 0 8px;
  }

  .fleet-lock-icon {
    color: #3182ce;
    width: 30px;
    height: 25px;
    margin: 0 14px;
  }
`;

const CenteredContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh; /* Ajusta la altura según tus necesidades */
`;

const TableWrap = styled.div`
  flex: 1 0 25%;

  a {
    padding: 10px 0 10px 10px;
    font-size: 12px;
    color: #3182ce;
  }

  img {
    width: 20px;
  }

  .premium {
    color: #ffbb0f;
  }

  .no-premium {
    color: gray;
  }

  .on-the-road {
    color: #ff7979;
    margin-bottom: 5px;
  }

  .trucker-available {
    color: #19c482;
    margin-bottom: 5px;
  }

  .card-number {
    color: #a7a7a7;
    padding: 5px 0 0 1px;
  }
`;

const TruckersMapContainer = styled.div`
  flex: 1;
  z-index: 0;
`;

const TableControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MyFleet = () => {
  const toast = useToast();
  const addTruckerRef = useRef(null);
  const [truckerSelected, setTruckerSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [truckers, setTruckers] = useState([]);
  const [truckersFiltered, setTruckersFiltered] = useState([]);
  const [filterTrucker, setFilterTrucker] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [isTruckerPermissionModalOpen, setIsTruckerPermissionModalOpen] = useState(false);
  const [isAllPermissionModalOpen, setIsAllPermissionModalOpen] = useState(false);
  const [isWappModalOpen, setIsWappModalOpen] = useState(false);
  const [isAddTruckerModalOpen, setIsAddTruckerModalOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [truckerPermissions, setTruckerPermissions] = useState([]);
  const [carrierName, setCarrierName] = useState('');

  const playstorLink = 'https://humber.page.link/dQkA';

  const wappMessageText = `El transportista ${carrierName} te solicita que descargues la aplicación de Humber para que puedas tener prioridad en las cargas. ${playstorLink}`;

  // Carga inicial de Datos del Transportista.-
  const fetchUserData = useCallback(async () => {
    try {
      const { data } = await client.endpoints.carrier.getUserData();
      const { legalName } = data;

      setCarrierName(legalName);
    } catch (error) {
      console.error(error);
      toast({
        title: <Text ml="10px">Error al obtener información del transportista</Text>,
        status: 'error',
        isClosable: true,
      });
    }
  }, []);

  // Carga inicial de camioneros (truckers).-
  const fetchTruckers = useCallback(async () => {
    try {
      const {
        data: { count, docs },
      } = await client.endpoints.carrier.getTruckers();

      if (count > 0) {
        const ids = docs.map(t => t.id).join(',');
        const { data: cards } = await client.endpoints.carrier.getTruckersCards(ids);

        docs.forEach(trucker => {
          const cardsForTrucker = cards.find(card => card.id === trucker.id);

          if (cardsForTrucker) {
            Object.assign(trucker, { cards: cardsForTrucker });
          }
        });
      }

      setTruckers(docs.sort((a, b) => b.onTheRoad - a.onTheRoad));
    } catch (error) {
      console.error(error);
      toast({
        title: <Text ml="10px">Error al obtener flota</Text>,
        status: 'error',
        isClosable: true,
      });
    }
  }, []);

  // Carga inicial de Permisos
  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await client.endpoints.carrier.getPermissions();

      setPermissions(data);
      setTruckerPermissions(data.map(permission => ({ ...permission, checked: false })));
    } catch (error) {
      console.error(error);
      toast({
        title: <Text ml="10px">Error al obtener permisos de flota</Text>,
        status: 'error',
        isClosable: true,
      });
    }
  }, []);

  // Carga inicial de información.-
  const initialLoad = useCallback(async () => {
    setLoading(true);

    await fetchUserData();
    await fetchTruckers();
    await fetchPermissions();

    setLoading(false);
  }, [fetchUserData, fetchTruckers, fetchPermissions]);

  useEffect(() => {
    initialLoad();
  }, [initialLoad]);

  useEffect(() => {
    if (truckers) {
      setTruckersFiltered(
        filterTrucker
          ? truckers.filter(trucker =>
              String(trucker.name)
                .toLowerCase()
                .includes(filterTrucker.toLowerCase())
            )
          : truckers
      );
    }
  }, [filterTrucker, truckers]);

  useEffect(() => {
    if (truckersFiltered.length) {
      setTruckerSelected(truckersFiltered[0]);
    }
  }, [truckersFiltered]);

  const closeWappModal = () => {
    setIsWappModalOpen(false);
  };

  // Enviar mensaje de invitación a choferes para que se instalen la app.-
  const sendWapp = async () => {
    try {
      setIsLoading(true);

      const userIds = truckers.map(trucker => trucker.id);
      await client.endpoints.carrier.sendWappMessage(wappMessageText, userIds);

      toast({
        title: <Text ml="10px">Mensajes enviados correctamente</Text>,
        status: 'success',
        duration: 9000,
        isClosable: true,
      });

      closeWappModal();
    } catch (error) {
      console.error(error);

      toast({
        title: <Text ml="10px">Error al enviar mensajes de invitación a choferes</Text>,
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Switch de permisos.-
  const handleCheckboxChange = useCallback(
    permission => {
      const updatedPermissions = truckerPermissions.map(item => {
        if (item.id === permission.id) {
          // eslint-disable-next-line no-param-reassign
          item.checked = !item.checked;
        }
        return item;
      });
      setTruckerPermissions(updatedPermissions);
    },
    [truckerPermissions]
  );

  // Permisos Generales.-
  const openAllPermissionModal = () => {
    setTruckerPermissions(permissions.map(permission => ({ ...permission, checked: false })));
    setIsAllPermissionModalOpen(true);
  };

  const closeAllPermissionModal = () => {
    setIsAllPermissionModalOpen(false);
  };

  const updateAllFleetPermissions = useCallback(async () => {
    try {
      setIsLoading(true);

      await Promise.all(
        truckers.map(async trucker => {
          const { id } = trucker;

          const permissionsToSave = truckerPermissions
            .filter(permission => permission.checked)
            .map(permission => permission.id);

          await client.endpoints.carrier.updatePermissions(id, permissionsToSave);
          return { id, updatedPermissions: permissionsToSave };
        })
      );

      toast({
        title: <Text ml="10px">Permisos de toda la flota actualizados correctamente</Text>,
        status: 'success',
        duration: 9000,
      });

      closeAllPermissionModal();
    } catch (error) {
      console.error(error);
      toast({
        title: <Text ml="10px">Error al actualizar los permisos de toda la flota</Text>,
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [truckerPermissions]);

  // Permisos particulares.-
  const openTruckerPermissionModal = async trucker => {
    try {
      const { id } = trucker;
      setTruckerSelected(trucker);
      const res = await client.endpoints.carrier.getPermissionsByUserId(id);

      const updatedTruckerPermissions = permissions.map(permission => ({
        ...permission,
        checked: res.data.some(item => item.id === permission.id),
      }));

      setTruckerPermissions(updatedTruckerPermissions);
      setIsTruckerPermissionModalOpen(true);
    } catch (error) {
      console.error(error);
      toast({
        title: (
          <Text ml="10px">
            Error al obtener los permisos particulares del chofer {trucker.name}
          </Text>
        ),
        status: 'error',
        isClosable: true,
      });
    }
  };

  const closeTruckerPermissionModal = () => {
    setIsTruckerPermissionModalOpen(false);
  };

  const updateTruckerPermissions = useCallback(async () => {
    try {
      setIsLoading(true);

      const { id, name } = truckerSelected;
      const permissionsToSave = truckerPermissions
        .filter(permission => permission.checked)
        .map(permission => permission.id);

      await client.endpoints.carrier.updatePermissions(id, permissionsToSave);

      toast({
        title: <Text ml="10px">Permisos de {name} actualizados correctamente</Text>,
        status: 'success',
        duration: 9000,
      });

      closeTruckerPermissionModal();
    } catch (error) {
      console.error(error);
      toast({
        title: (
          <Text ml="10px">Error al actualizar los permisos del chofer {truckerSelected.name}</Text>
        ),
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [truckerSelected, truckerPermissions]);

  // Agregar nuevo chofer.
  const openAddTruckerModal = () => {
    setIsAddTruckerModalOpen(true);
  };

  const closeAddTruckerModal = () => {
    setIsAddTruckerModalOpen(false);
    fetchTruckers();
  };

  const [addTruckerState, setAddTruckerState] = useState({ loading: false });

  const handleAddTruckerStateChange = useCallback(state => {
    setAddTruckerState(state);
  }, []);

  const handleSaveTrucker = () => {
    if (addTruckerRef.current) {
      addTruckerRef.current.saveTrucker();
    }
  };

  // Edición.-
  const onOpenModalHandle = useCallback(e => {
    e.preventDefault();
    setOpenModal(true);
  }, []);

  const listItemStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 20px 10px 0',
    borderBottom: '1px solid gray',
    margin: '10px',
  };

  const wappModalHeaderStyles = {
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <Container>
      <InfoBar
        status="info"
        text="Descargá la aplicación de Humber en los dispositivos de tus choferes - Enviá el link de descarga por whatsapp."
        setIsWappModalOpen={setIsWappModalOpen}
      />
      {loading && (
        <CenteredContainer>
          <Spinner thickness="4px" speed="0.65s" emptyColor="#383d54" color="#ffbb0f" size="xl" />
        </CenteredContainer>
      )}
      <TableWrap>
        <TableControls>
          <Input
            w="400px"
            m="10px 0 10px 65px"
            variant="flushed"
            placeholder="Buscar... "
            size="lg"
            id="search"
            type="text"
            value={filterTrucker}
            onChange={event => setFilterTrucker(event.target.value)}
          />

          <Box display="flex" gap={3}>
            <Button m="27px" onClick={openAddTruckerModal} size="md" colorScheme="blue">
              Agregar Humber
              <FontAwesomeIcon cursor="pointer" className="fleet-lock-icon" icon={faUserPlus} />
            </Button>
            <Button m="27px" onClick={openAllPermissionModal} size="md">
              Gestionar permisos para toda la flota
              <FontAwesomeIcon
                cursor="pointer"
                className="fleet-lock-icon"
                icon={faUserLock}
                onClick={openAllPermissionModal}
              />
            </Button>
          </Box>
        </TableControls>
        <TableContainer>
          <Table variant="simple" size="sm" className="table-tiny">
            <Thead>
              <Tr>
                <Th />
                <Th>Chofer</Th>
                <Th>CUIT</Th>
                <Th>Patente</Th>
                <Th>Tarjeta Humber</Th>
                <Th>Nro. celular</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {truckersFiltered.map(trucker => (
                <Tr key={trucker.id}>
                  <Td>
                    <FontAwesomeIcon
                      className={trucker.premium ? 'premium' : 'no-premium'}
                      icon={faCrown}
                    />
                  </Td>
                  <Td maxW="500">
                    <Text
                      fontSize="xs"
                      className={trucker.onTheRoad ? 'on-the-road' : 'trucker-available'}
                    >
                      {trucker.onTheRoad ? 'En viaje' : 'Disponible'}
                    </Text>
                    <Text fontSize="md">{trucker.name}</Text>
                  </Td>
                  <Td>{trucker.cuit}</Td>
                  <Td>{trucker.truck?.plate?.replace(/"/g, '').trim() || ''}</Td>
                  <Td>
                    <Text fontSize="md">
                      {trucker.cards?.hasOwnProperty('bambaCardBalance')
                        ? `$ ${trucker.cards.bambaCardBalance}`
                        : ''}
                    </Text>
                    <Text fontSize="xs" className="card-number">
                      {trucker.cards?.hasOwnProperty('bambaCardNumber')
                        ? `Nro. ${trucker.cards.bambaCardNumber}`
                        : ''}
                    </Text>
                  </Td>
                  <Td>{trucker.phoneNumber}</Td>
                  <Td>
                    <FontAwesomeIcon
                      title="Editar"
                      className="icon"
                      cursor="pointer"
                      icon={faEdit}
                      onClick={e => {
                        setTruckerSelected(trucker);
                        onOpenModalHandle(e);
                      }}
                    />
                    <FontAwesomeIcon
                      title="Ver en mapa"
                      className="icon"
                      icon={faMapMarkedAlt}
                      cursor="pointer"
                      onClick={() => setTruckerSelected(trucker)}
                    />
                    <FontAwesomeIcon
                      title="Permisos del camionero"
                      className="icon"
                      icon={faLock}
                      cursor="pointer"
                      onClick={() => openTruckerPermissionModal(trucker)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </TableWrap>

      <Modal isOpen={isAllPermissionModalOpen} onClose={closeAllPermissionModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Permisos para toda mi flota</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UnorderedList>
              {permissions.map(item => (
                <ListItem key={item.id}>
                  <Box sx={listItemStyles}>
                    <p>{item.description}</p>
                    <Switch
                      size="md"
                      isChecked={item.checked}
                      onChange={() => handleCheckboxChange(item)}
                      defaultChecked={false}
                    />
                  </Box>
                </ListItem>
              ))}
            </UnorderedList>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeAllPermissionModal}>
              Cerrar
            </Button>
            <Button isLoading={isLoading} colorScheme="blue" onClick={updateAllFleetPermissions}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isTruckerPermissionModalOpen} onClose={closeTruckerPermissionModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Permisos de {truckerSelected.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UnorderedList>
              {truckerPermissions.map(item => (
                <ListItem key={item.id}>
                  <Box sx={listItemStyles}>
                    <p>{item.description}</p>
                    <Switch
                      size="md"
                      isChecked={item.checked}
                      onChange={() => handleCheckboxChange(item)}
                      defaultChecked={item.checked}
                    />
                  </Box>
                </ListItem>
              ))}
            </UnorderedList>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeTruckerPermissionModal}>
              Cancelar
            </Button>
            <Button isLoading={isLoading} colorScheme="blue" onClick={updateTruckerPermissions}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isWappModalOpen} onClose={closeWappModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Box sx={wappModalHeaderStyles}>
              <FontAwesomeIcon icon={faWhatsapp} />
              <Text m="10px" fontSize="md">
                Enviar invitación a tus choferes
              </Text>
            </Box>
            <Text ml="27px" fontSize="sm" color="darkgray">
              Los camioneros que ya tienen la app no van a recibir este mensaje
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea p="20px" h="120px" value={wappMessageText} readOnly />
          </ModalBody>
          <ModalFooter>
            <Button isLoading={isLoading} colorScheme="whatsapp" onClick={sendWapp}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isAddTruckerModalOpen}
        onClose={closeAddTruckerModal}
        size="6xl"
        scrollBehavior="inside"
        blockScrollOnMount
        preserveScrollBarGap
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" bg="#31354e" position="relative">
          {addTruckerState.loading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="blackAlpha.700"
              zIndex={10}
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="md"
            >
              <Spinner thickness="4px" speed="0.65s" emptyColor="#383d54" color="#ffbb0f" size="xl" />
            </Box>
          )}
          <ModalHeader color="white" borderBottom="1px solid #1f2231">
            Agregar Humber
          </ModalHeader>
          <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />
          <ModalBody overflowY="auto" p={4}>
            <AddTrucker
              ref={addTruckerRef}
              onSuccess={closeAddTruckerModal}
              onStateChange={handleAddTruckerStateChange}
              isInModal
            />
          </ModalBody>
          <ModalFooter bg="#1f2231" borderTop="1px solid #383d54">
            {addTruckerState.truckerCreated ? (
              <Button colorScheme="yellow" onClick={closeAddTruckerModal}>
                Cerrar
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={closeAddTruckerModal} color="white" mr={3}>
                  Cancelar
                </Button>
                <Button
                  colorScheme="yellow"
                  onClick={handleSaveTrucker}
                  isLoading={addTruckerState.loading}
                >
                  Guardar
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <TruckersMapContainer>
        {truckerSelected && <TruckersMap truckerSelected={truckerSelected} />}
      </TruckersMapContainer>
      {openModal ? (
        <EditTruckerModal
          trucker={truckerSelected}
          premiumPhotos={truckerSelected.premiumPhotos}
          openModal={openModal}
          setOpenModal={setOpenModal}
          fetchTruckerList={fetchTruckers}
        />
      ) : (
        ''
      )}
    </Container>
  );
};

export default MyFleet;
